// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Settings → AI Usage & Cost (**ADR-0080**).
 *
 * **Estimated, never invoiced.** Every figure is labelled as an estimate,
 * because it is: a cost computed from token counts against a stored rate is not
 * a bill, and a screen that implied otherwise would be the wrong kind of
 * confident.
 *
 * **Currencies are shown side by side, never summed.** Two providers billed in
 * different currencies produce two totals. There is no place on this screen for
 * a combined number, and no field in the API that could supply one.
 *
 * **Unpriced attempts are shown prominently, never as zero.** Real activity
 * whose cost nobody could resolve is the thing a reader most needs to know
 * about, because it is exactly what a total does not account for.
 *
 * **Times are UTC, and the screen says so.** The API boundary is UTC; the
 * heading names the zone rather than leaving a reader to assume their own.
 *
 * The chart is inline SVG rather than a charting dependency: this is one bounded
 * series of daily bars, and a new runtime dependency is a supply-chain decision
 * that a bar chart does not justify.
 */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import {
  attemptOutcomes,
  attemptRoles,
  capabilities,
  hasCapability,
  providerLabels,
  providerTypes,
  useApiClients,
  useSession,
  type DailyCost,
  type UsageFilters,
} from '@eai/ui-shared';

/** Said wherever a number could be mistaken for a bill. */
export const ESTIMATE_NOTICE =
  'Estimated cost, calculated from recorded usage against the pricing in force when each ' +
  'call happened. Not an invoice.';

/** Said when a window contains activity nobody could price. */
export const UNPRICED_NOTICE =
  'Some attempts have no pricing and are excluded from the totals below. They are real ' +
  'usage with an unknown cost, not free usage.';

/** The zone every instant on this screen is expressed in. */
export const DISPLAY_TIMEZONE = 'UTC';

/**
 * Render one fixed-precision amount for display.
 *
 * Trims the trailing precision Platform publishes without parsing the value:
 * `Number()` on a monetary string is the bug this whole screen avoids.
 *
 * @param amount - The fixed-precision decimal string.
 * @returns The amount at two decimal places, as text.
 */
export function displayAmount(amount: string): string {
  const [whole = '0', fraction = ''] = amount.split('.');
  return `${whole}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * Return the first instant of the current UTC month, as a date input value.
 *
 * @param now - The current instant.
 * @returns An ISO date, `YYYY-MM-DD`.
 */
export function monthStart(now: Date): string {
  const year = String(now.getUTCFullYear());
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

/**
 * Return the first instant of the next UTC month, as a date input value.
 *
 * @param now - The current instant.
 * @returns An ISO date, `YYYY-MM-DD`.
 */
export function monthEnd(now: Date): string {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return next.toISOString().slice(0, 10);
}

/** A dependency-free daily bar chart. */
function TrendChart({ days }: { readonly days: readonly DailyCost[] }): ReactNode {
  // Compared as strings would sort wrongly, so the bar heights come from a
  // Number() of the *already-displayed* value. That is safe: this is a pixel
  // height, not money, and nothing downstream reads it back as a cost.
  const heights = days.map((day) => Number(day.estimated_cost));
  const tallest = Math.max(...heights, 0);
  const width = Math.max(days.length * 24, 24);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg
        role="img"
        aria-label={`Daily estimated cost, ${String(days.length)} days, in ${DISPLAY_TIMEZONE}`}
        viewBox={`0 0 ${String(width)} 120`}
        style={{ width: '100%', minWidth: `${String(width)}px`, height: 160 }}
      >
        {days.map((day, index) => {
          const value = heights[index] ?? 0;
          const height = tallest === 0 ? 0 : (value / tallest) * 100;
          return (
            <rect
              key={`${day.day}-${day.currency}`}
              x={index * 24 + 4}
              y={110 - height}
              width={16}
              height={Math.max(height, 1)}
              fill="currentColor"
              opacity={0.7}
            >
              <title>{`${day.day} — ${day.currency} ${displayAmount(day.estimated_cost)}`}</title>
            </rect>
          );
        })}
      </svg>
    </Box>
  );
}

/** Render the AI usage and cost screen. */
export function AiUsageScreen(): ReactNode {
  const clients = useApiClients();
  const { session } = useSession();
  const canRead = hasCapability(session, capabilities.aiUsageRead);

  const now = useMemo(() => new Date(), []);
  const [start, setStart] = useState(monthStart(now));
  const [end, setEnd] = useState(monthEnd(now));
  const [collegeId, setCollegeId] = useState('');
  const [providerType, setProviderType] = useState('');
  const [modelRef, setModelRef] = useState('');
  const [role, setRole] = useState('');
  const [outcome, setOutcome] = useState('');

  const filters: UsageFilters = useMemo(
    () => ({
      start: start === '' ? undefined : `${start}T00:00:00Z`,
      end: end === '' ? undefined : `${end}T00:00:00Z`,
      college_id: collegeId === '' ? undefined : collegeId,
      provider_type: providerType === '' ? undefined : providerType,
      provider_model_ref: modelRef === '' ? undefined : modelRef,
      role: role === '' ? undefined : (role as UsageFilters['role']),
      outcome: outcome === '' ? undefined : (outcome as UsageFilters['outcome']),
    }),
    [start, end, collegeId, providerType, modelRef, role, outcome],
  );

  // One query key per filter set, so changing a filter refetches rather than
  // showing yesterday's number under today's heading.
  const usage = useQuery({
    queryKey: ['ai-usage', filters],
    enabled: canRead,
    queryFn: async () => {
      const [summary, colleges, breakdown, trend] = await Promise.all([
        clients.aiUsage.summary(filters),
        clients.aiUsage.byCollege(filters),
        clients.aiUsage.breakdown(filters),
        clients.aiUsage.trend(filters),
      ]);
      return { summary, colleges, breakdown, trend };
    },
  });

  if (!canRead) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4">AI Usage &amp; Cost</Typography>
        <Alert severity="info">
          You do not have permission to view AI usage and cost for this University.
        </Alert>
      </Stack>
    );
  }

  const summary = usage.data?.summary;
  const colleges = usage.data?.colleges.items ?? [];
  const breakdown = usage.data?.breakdown.items ?? [];
  const trend = usage.data?.trend.items ?? [];
  const empty = usage.isSuccess && summary?.attempts === 0;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4">AI Usage &amp; Cost</Typography>
        <Typography variant="body2" color="text.secondary">
          {ESTIMATE_NOTICE} All dates and times are shown in {DISPLAY_TIMEZONE}.
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
            <TextField
              label="From"
              type="date"
              value={start}
              onChange={(event) => {
                setStart(event.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="To"
              type="date"
              value={end}
              onChange={(event) => {
                setEnd(event.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="College"
              value={collegeId}
              onChange={(event) => {
                setCollegeId(event.target.value);
              }}
            />
            <TextField
              select
              label="Provider"
              value={providerType}
              onChange={(event) => {
                setProviderType(event.target.value);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All providers</MenuItem>
              {providerTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {providerLabels[type]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Model"
              value={modelRef}
              onChange={(event) => {
                setModelRef(event.target.value);
              }}
            />
            <TextField
              select
              label="Role"
              value={role}
              onChange={(event) => {
                setRole(event.target.value);
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Primary and fallback</MenuItem>
              {attemptRoles.map((value) => (
                <MenuItem key={value} value={value}>
                  {value === 'primary' ? 'Primary only' : 'Fallback only'}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Outcome"
              value={outcome}
              onChange={(event) => {
                setOutcome(event.target.value);
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All outcomes</MenuItem>
              {attemptOutcomes.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            <Button
              component="a"
              href={clients.aiUsage.exportHref(filters)}
              download
              variant="outlined"
            >
              Export CSV
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {usage.isError ? (
        <Alert severity="error">
          AI usage could not be loaded. The figures below are not shown rather than shown
          incomplete.
        </Alert>
      ) : null}

      {usage.isPending ? <Typography variant="body2">Loading usage…</Typography> : null}

      {empty ? (
        <Alert severity="info">
          No AI usage was recorded in this window. Try a wider date range or clear the filters.
        </Alert>
      ) : null}

      {summary !== undefined && summary.unpriced_attempts > 0 ? (
        <Alert severity="warning">
          {UNPRICED_NOTICE} Unpriced attempts in this window: {summary.unpriced_attempts}.
        </Alert>
      ) : null}

      {summary !== undefined && !empty ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Totals
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap">
              {summary.totals.length === 0 ? (
                <Typography variant="body2">No priced activity in this window.</Typography>
              ) : (
                summary.totals.map((total) => (
                  <Box key={total.currency}>
                    <Typography variant="overline">{total.currency} estimated</Typography>
                    <Typography variant="h5">{displayAmount(total.estimated_cost)}</Typography>
                    <Typography variant="caption" display="block">
                      Primary {displayAmount(total.primary_cost)} · Fallback{' '}
                      {displayAmount(total.fallback_cost)}
                    </Typography>
                  </Box>
                ))
              )}
              <Box>
                <Typography variant="overline">Provider calls</Typography>
                <Typography variant="h5">{summary.attempts}</Typography>
                <Typography variant="caption" display="block">
                  {summary.succeeded} succeeded · {summary.failed} failed · {summary.cancelled}{' '}
                  cancelled
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline">Tokens</Typography>
                <Typography variant="body2">In {summary.input_tokens}</Typography>
                <Typography variant="body2">Out {summary.output_tokens}</Typography>
                <Typography variant="body2">Cached {summary.cached_tokens}</Typography>
                <Typography variant="body2">Reasoning {summary.reasoning_tokens}</Typography>
              </Box>
              <Box>
                <Typography variant="overline">Unpriced attempts</Typography>
                <Typography variant="h5">{summary.unpriced_attempts}</Typography>
                <Chip size="small" label="not counted in totals" />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {trend.length > 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Daily estimated cost ({DISPLAY_TIMEZONE})
            </Typography>
            <TrendChart days={trend} />
          </CardContent>
        </Card>
      ) : null}

      {colleges.length > 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cost by college
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Estimated cost by college">
                <TableHead>
                  <TableRow>
                    <TableCell>College</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Estimated cost</TableCell>
                    <TableCell align="right">Unpriced</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {colleges.map((row) => (
                    <TableRow key={`${row.college_id}-${row.currency}`}>
                      <TableCell>{row.college_id}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell align="right">{row.attempts}</TableCell>
                      <TableCell align="right">{displayAmount(row.estimated_cost)}</TableCell>
                      <TableCell align="right">{row.unpriced_attempts}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {breakdown.length > 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cost by provider and model
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Estimated cost by provider and model">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Succeeded</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Estimated cost</TableCell>
                    <TableCell align="right">Unpriced</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdown.map((row) => (
                    <TableRow
                      key={`${row.provider_type}-${row.provider_model_ref}-${row.currency}`}
                    >
                      <TableCell>{row.provider_type}</TableCell>
                      <TableCell>{row.provider_model_ref}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell align="right">{row.attempts}</TableCell>
                      <TableCell align="right">{row.succeeded}</TableCell>
                      <TableCell align="right">{row.failed}</TableCell>
                      <TableCell align="right">{displayAmount(row.estimated_cost)}</TableCell>
                      <TableCell align="right">{row.unpriced_attempts}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
}
